import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiUser, FiFlag, FiTarget, FiTrendingUp, FiChevronDown,
  FiPlusCircle, FiEdit3, FiTrash2, FiMapPin,
} from 'react-icons/fi';
import { useApi } from '../../useApi';
import './AuditLogList.css';

const TABLE_META = {
  player: { label: 'Player', icon: FiUser },
  course: { label: 'Course', icon: FiFlag },
  tee: { label: 'Tee', icon: FiMapPin },
  hole_score: { label: 'Hole score', icon: FiTarget },
  handicap_adjustment: { label: 'Handicap rule', icon: FiTrendingUp },
  // System-admin only: a plan changed by hand (audit.py, User).
  user: { label: 'User plan', icon: FiUser },
};

const TABLE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'player', label: 'Players' },
  { key: 'hole_score', label: 'Scores' },
  { key: 'course', label: 'Courses' },
  { key: 'tee', label: 'Tees' },
  { key: 'handicap_adjustment', label: 'H’cap rules' },
];

const ACTION_META = {
  INSERT: { label: 'Added', cls: 'audit-action--added', icon: FiPlusCircle },
  UPDATE: { label: 'Changed', cls: 'audit-action--changed', icon: FiEdit3 },
  DELETE: { label: 'Deleted', cls: 'audit-action--deleted', icon: FiTrash2 },
};

const WINDOW_FILTERS = [
  { key: '7', label: '7d' },
  { key: '30', label: '30d' },
  { key: '90', label: '90d' },
  { key: '0', label: 'All' },
];

// Snapshot keys that add noise without telling the reader anything
const HIDDEN_SNAPSHOT_KEYS = new Set(['id', 'society_id', 'global_course_id', 'round_id']);

const prettyField = (key) =>
  key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const prettyValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return String(v);
};

const formatWhen = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Best human label for the affected record: the server-resolved name when the
// record still exists, then a name from the change payload (covers deletes),
// otherwise the record id.
const recordLabel = (log) => {
  if (log.record_label) return log.record_label;
  const c = log.changes;
  if (c && typeof c === 'object') {
    if (typeof c.name === 'string' && c.name) return c.name;
    if (c.name && typeof c.name === 'object' && (c.name.new || c.name.old)) {
      return c.name.new || c.name.old;
    }
    if (log.table_name === 'hole_score') {
      const hole = c.hole_number && typeof c.hole_number === 'object'
        ? (c.hole_number.new ?? c.hole_number.old)
        : c.hole_number;
      if (hole) return `Hole ${hole}`;
    }
    if (log.table_name === 'handicap_adjustment' && c.value !== undefined && typeof c.value !== 'object') {
      return `${c.adjustment_type === 'POSITION' ? 'Position' : 'Score'} ${c.value}`;
    }
  }
  return `#${log.record_id}`;
};

// Compact "what actually changed" for the collapsed card header. Only when it
// fits at a glance: single-field updates with short values, or the gross
// score of an added/deleted hole score.
const MAX_INLINE_LEN = 12;
const fitsInline = (v) => String(v ?? '').length <= MAX_INLINE_LEN;

const inlineChange = (log) => {
  const c = log.changes;
  if (!c || typeof c !== 'object') return null;
  if (log.action === 'UPDATE') {
    const entries = Object.entries(c);
    if (entries.length !== 1) return null;
    const [field, vals] = entries[0];
    if (!vals || typeof vals !== 'object') return null;
    if (!fitsInline(vals.old) || !fitsInline(vals.new)) return null;
    // For hole scores the value is self-evidently the score
    return { field: log.table_name === 'hole_score' ? null : field, old: vals.old, new: vals.new };
  }
  if (log.table_name === 'hole_score' && c.gross_score !== undefined) {
    return log.action === 'INSERT'
      ? { field: null, new: c.gross_score }
      : { field: null, old: c.gross_score };
  }
  return null;
};

const DiffRows = ({ log }) => {
  const c = log.changes;
  if (!c || typeof c !== 'object') return null;

  if (log.action === 'UPDATE') {
    return (
      <div className="audit-diff">
        {Object.entries(c).map(([field, vals]) => (
          <div key={field} className="audit-diff-row">
            <span className="audit-diff-field">{prettyField(field)}</span>
            <span className="audit-diff-vals">
              <span className="audit-val audit-val--old">{prettyValue(vals?.old)}</span>
              <span className="audit-arrow">{'→'}</span>
              <span className="audit-val audit-val--new">{prettyValue(vals?.new)}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }

  // INSERT / DELETE: snapshot of the record
  const entries = Object.entries(c).filter(
    ([k, v]) => !HIDDEN_SNAPSHOT_KEYS.has(k) && v !== null && v !== undefined && v !== ''
  );
  return (
    <div className="audit-diff">
      {entries.map(([field, val]) => (
        <div key={field} className="audit-diff-row">
          <span className="audit-diff-field">{prettyField(field)}</span>
          <span className="audit-diff-vals">
            <span className="audit-val">{prettyValue(val)}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

// HOW MANY ROWS THIS LIST WILL EVER HOLD.
//
// "Load more" used to append with no ceiling, so on a society with a few years
// of trail behind it the list was only as long as the reader's patience — and
// every row stays mounted, expandable, and re-rendered on every subsequent
// page. Eight pages is already far more than anyone reads in order; past that
// the honest answer is that scrolling is the wrong tool and a filter is the
// right one, so the list says so instead of fetching a ninth.
const MAX_ROWS = 200;
const PER_PAGE = 25;

/**
 * Shared audit trail list.
 * Props:
 *  - endpoint: base URL ('/admin/audit-logs' or '/societies/<id>/audit-logs')
 *  - showSociety: include the society name line (system-admin view)
 */
const AuditLogList = ({ endpoint, showSociety = false }) => {
  const { authenticatedFetch } = useApi();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [tableFilter, setTableFilter] = useState('');
  const [windowDays, setWindowDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Monotonic id so a slow response can't clobber a newer one after rapid
  // filter/window toggles.
  const fetchSeq = useRef(0);

  // `before` is a keyset cursor, not a page number: the server seeks straight
  // to the next 25 older than the last row shown, so the tenth Load more costs
  // what the first did. Absent, it means the top of the list.
  const fetchPage = useCallback(async (before, table, days, append) => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ per_page: String(PER_PAGE), days });
      if (before) params.set('before', before);
      if (table) params.set('table_name', table);
      const res = await authenticatedFetch(`${endpoint}?${params.toString()}`);
      if (seq !== fetchSeq.current) return;
      if (!res.ok) {
        setError(res.status === 403
          ? 'You need admin access to view the activity log.'
          : 'Could not load the activity log. Please try again.');
        return;
      }
      const data = await res.json();
      if (seq !== fetchSeq.current) return;
      // Dedupe on append: rows written between page fetches shift the
      // boundaries, so the next page can repeat entries we already have.
      setItems(prev => (append
        ? [...prev, ...data.items.filter(item => !prev.some(p => p.id === item.id))]
        : data.items));
      setCursor(data.next_cursor ?? null);
      setHasMore(!!data.has_more);
      setTotal(data.total ?? 0);
      setTotalCapped(!!data.total_capped);
    } catch {
      if (seq === fetchSeq.current) setError('Could not load the activity log. Please try again.');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [authenticatedFetch, endpoint]);

  useEffect(() => {
    fetchPage(null, tableFilter, windowDays, false);
  }, [fetchPage, tableFilter, windowDays]);

  // Past COUNT_CAP the server stops counting, so the total is a floor.
  const totalLabel = totalCapped ? `${total}+` : total;
  const atCeiling = items.length >= MAX_ROWS;

  return (
    <div className="audit-list">
      <div className="audit-filters">
        {TABLE_FILTERS.map(f => (
          <button
            key={f.key}
            className={`audit-filter-chip${tableFilter === f.key ? ' audit-filter-chip--active' : ''}`}
            onClick={() => { setExpandedId(null); setTableFilter(f.key); }}
          >
            {f.label}
          </button>
        ))}
        <span className="audit-window">
          {WINDOW_FILTERS.map(w => (
            <button
              key={w.key}
              className={`audit-filter-chip audit-filter-chip--window${windowDays === w.key ? ' audit-filter-chip--active' : ''}`}
              onClick={() => { setExpandedId(null); setWindowDays(w.key); }}
            >
              {w.label}
            </button>
          ))}
        </span>
      </div>

      {error && <div className="audit-error">{error}</div>}

      {!error && !loading && items.length === 0 && (
        <div className="audit-empty">
          <FiEdit3 size={28} />
          <p>No changes recorded yet{tableFilter ? ' for this filter' : ''}.</p>
        </div>
      )}

      {items.map(log => {
        const tMeta = TABLE_META[log.table_name] || { label: log.table_name, icon: FiEdit3 };
        const aMeta = ACTION_META[log.action] || ACTION_META.UPDATE;
        const TIcon = tMeta.icon;
        const isOpen = expandedId === log.id;
        const inline = inlineChange(log);
        return (
          <div key={log.id} className={`audit-card${isOpen ? ' audit-card--open' : ''}`}>
            <button className="audit-card-head" onClick={() => setExpandedId(isOpen ? null : log.id)}>
              <span className={`audit-icon audit-icon--${log.table_name}`}><TIcon size={16} /></span>
              <span className="audit-main">
                <span className="audit-title">
                  <span className="audit-record">{recordLabel(log)}</span>
                  {inline && (
                    <span className="audit-inline">
                      {inline.field && <span className="audit-inline-field">{prettyField(inline.field)}</span>}
                      {inline.old !== undefined && <span className="audit-val audit-val--old">{prettyValue(inline.old)}</span>}
                      {inline.old !== undefined && inline.new !== undefined && <span className="audit-arrow">{'→'}</span>}
                      {inline.new !== undefined && <span className="audit-val audit-val--new">{prettyValue(inline.new)}</span>}
                    </span>
                  )}
                </span>
                <span className="audit-sub">
                  <span className="audit-sub-left">
                    <span className={`audit-action ${aMeta.cls}`}>{aMeta.label}</span>
                    {' · '}{log.user_name || 'Unknown user'}
                    {showSociety && log.society_name ? ` · ${log.society_name}` : ''}
                  </span>
                  <span className="audit-sub-time">{formatWhen(log.timestamp)}</span>
                </span>
              </span>
              <FiChevronDown size={16} className="audit-chevron" />
            </button>
            {isOpen && <DiffRows log={log} />}
          </div>
        );
      })}

      {loading && <div className="audit-loading">Loading{'…'}</div>}

      {!loading && hasMore && !atCeiling && (
        <button
          className="audit-load-more"
          onClick={() => fetchPage(cursor, tableFilter, windowDays, true)}
        >
          Load more ({items.length} of {totalLabel})
        </button>
      )}

      {!loading && hasMore && atCeiling && (
        <div className="audit-ceiling">
          Showing the newest {items.length} of {totalLabel} changes. Pick a type or a
          shorter window to look further back.
        </div>
      )}
    </div>
  );
};

export default AuditLogList;
